/**
 * @module experience/shop/shared-browse
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
import {
  CustomerGroup,
  SegmentationAttribute,
  SegmentationDetails,
} from '@support/api/proxies/responses/api-2021-segmentation.interface';
import {
  CustomerInfoFormValue,
  SegmentationRequestFormValue,
} from '@experience/shop/cart/modules/account-setup/account-setup.interface';

export class AccountSetupSegmentationModel {
  /**
   * Segmentation data from api
   */
  private segmentationDetails: SegmentationDetails;

  constructor(segmentationDetails: SegmentationDetails) {
    this.segmentationDetails = segmentationDetails;
  }

  /**
   * Function to extract default customer group for customer or account
   * @param {SegmentationDetails} customerSegmentationData
   * @returns {SegmentationRequestFormValue}
   */
  public extractCustomerAccountDefaultData(
    customerSegmentationData: SegmentationDetails
  ): SegmentationRequestFormValue {
    return {
      customer: this.extractDefaultCustomerGroupData(
        customerSegmentationData.segmentation.customerSegmentation.customerGroup
      ),
      account: this.extractDefaultCustomerGroupData(
        customerSegmentationData.segmentation.accountSegmentation.customerGroup
      ),
    };
  }

  /**
   * Function to default data for each customer group
   * @param {CustomerGroup[]} customerData
   * @returns {CustomerInfoFormValue}
   */
  public extractDefaultCustomerGroupData(customerData: CustomerGroup[]): CustomerInfoFormValue {
    const defaultCustomerGroup = customerData.find((cd: CustomerGroup) => {
      return cd.isDefault === true;
    });
    const customerInfo: CustomerInfoFormValue =
      {
        group: defaultCustomerGroup.attributeValue,
        test: this.extractDefaultAttributeValue(defaultCustomerGroup.testIndicator),
        idVerification: this.extractDefaultAttributeValue(defaultCustomerGroup.idVerification),
        assistance: this.extractDefaultAttributeValue(defaultCustomerGroup.assistanceProgram),
        specialTreatment: this.extractDefaultAttributeValue(defaultCustomerGroup.specialTreatment),
      } || {};
    return customerInfo;
  }

  /**
   * Function to extract default segmentation attribute
   * @param {SegmentationAttribute[]} segmentationAttributes
   * @returns {SegmentationAttribute}
   */
  public extractDefaultAttributeValue(
    segmentationAttributes: SegmentationAttribute[]
  ): SegmentationAttribute {
    let segmentationAttribute: SegmentationAttribute = {};
    if (segmentationAttributes) {
      segmentationAttribute =
        segmentationAttributes.find((sa: SegmentationAttribute) => {
          return sa.isDefault === true;
        }) || {};
    }
    return segmentationAttribute;
  }

  /**
   * Function to get customer or account group data from api response
   * @param {boolean} isCustomerGroup
   * @returns {CustomerGroup[]}
   */
  public getCustomerOrAccountGroupData(isCustomerGroup: boolean): CustomerGroup[] {
    const recordType: string = isCustomerGroup ? 'customerSegmentation' : 'accountSegmentation';
    const customerGroup: CustomerGroup[] =
      this.segmentationDetails.segmentation[recordType].customerGroup || [];
    return customerGroup;
  }
}
